import { useState, type ReactElement } from 'react';
import { api, type ImportReport } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { codeLabel } from '../shared/lib/status';
import { Card, primaryButtonStyle, secondaryButtonStyle } from '../shared/ui/primitives';

/**
 * Импорт заказов из CSV (разд. 11, FR-578).
 *
 * Предпросмотр по умолчанию и только потом применение. Импорт запускают в нештатной
 * ситуации и в спешке; схема, при которой случайно отправленный файл сразу заводит
 * заказы, — вопрос времени, а не вероятности.
 *
 * Ошибочные строки подняты наверх с номером строки, полем и кодом: применяются только
 * корректные, остальные возвращаются на исправление.
 */
export function ImportPage(): ReactElement {
  const messages = useMessages();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const run = async (dryRun: boolean): Promise<void> => {
    if (file === null) {
      return;
    }

    setPending(true);
    setError(null);

    try {
      setReport(await api.importCsv(file, dryRun));
    } catch (cause) {
      setError(String(cause));
    } finally {
      setPending(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: '860px' }}>
      <h1>{messages.cabinet.importCsv.title}</h1>

      <Card>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            setReport(null);
          }}
        />
        <span style={{ color: 'var(--text-muted)' }}>{messages.cabinet.importCsv.dryRunNote}</span>

        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <button
            type="button"
            style={secondaryButtonStyle}
            disabled={file === null || pending}
            onClick={() => void run(true)}
          >
            {messages.cabinet.importCsv.preview}
          </button>
          <button
            type="button"
            style={primaryButtonStyle}
            disabled={file === null || pending || report === null}
            onClick={() => void run(false)}
          >
            {messages.cabinet.importCsv.apply}
          </button>
        </div>
      </Card>

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}

      {report === null ? null : (
        <Card tone={report.rejected > 0 ? 'warn' : 'ok'}>
          <div style={{ display: 'flex', gap: 'var(--sp-4)' }}>
            <span>
              {`${messages.cabinet.importCsv.rows}: `}
              <span className="num">{report.total}</span>
            </span>
            <span style={{ color: 'var(--ok)' }}>
              {`${messages.cabinet.importCsv.valid}: `}
              <span className="num">{report.accepted}</span>
            </span>
            <span style={{ color: 'var(--danger)' }}>
              {`${messages.cabinet.importCsv.invalid}: `}
              <span className="num">{report.rejected}</span>
            </span>
          </div>

          {report.errors.length === 0 ? null : (
            <table>
              <tbody>
                {report.errors.map((row, index) => (
                  <tr key={`${row.line}-${row.field}-${index}`}>
                    <td className="num" style={cellStyle}>
                      {row.line}
                    </td>
                    <td style={cellStyle}>{row.field}</td>
                    <td style={{ ...cellStyle, color: 'var(--danger)' }}>
                      {codeLabel(messages.cabinet.importCsv.errors, row.code)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}

const cellStyle = { padding: 'var(--sp-1) var(--sp-3)', borderBottom: '1px solid var(--border)' };
